import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Star, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminReviews = ({ headers }) => {
  const [reviews, setReviews] = useState([]);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, { headers });
      setReviews(data);
    } catch {
      toast.error("Could not load reviews");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async (id) => {
    try {
      await axios.put(`${API}/admin/reviews/${id}/approve`, {}, { headers });
      toast.success("Review published");
      load();
    } catch {
      toast.error("Could not publish review");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers });
      toast.success("Review deleted");
      load();
    } catch {
      toast.error("Could not delete review");
    }
  };

  return (
    <section className="mt-16" data-testid="admin-reviews-section">
      <h2 className="font-display text-2xl text-[#1B3A6B]">Customer Reviews</h2>
      <p className="mt-2 text-sm text-[#67718A]">New reviews wait here until you approve them. Approved reviews show on the website.</p>
      {reviews.length === 0 ? (
        <p className="mt-6 text-sm italic text-[#67718A]" data-testid="admin-reviews-empty">No reviews yet.</p>
      ) : (
        <div className="mt-6 space-y-4">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`admin-review-${r.id}`} className="flex flex-col gap-4 rounded-sm border border-[#D4AF37]/25 bg-white p-5 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm font-semibold text-[#1B3A6B]">{r.name}</p>
                  {r.occasion && <p className="text-xs uppercase tracking-[0.16em] text-[#67718A]">{r.occasion}</p>}
                  <span className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={12} className={n <= r.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#D4AF37]/30"} />
                    ))}
                  </span>
                  <span
                    data-testid={`admin-review-status-${r.id}`}
                    className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.16em] ${r.approved ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}
                  >
                    {r.approved ? "Published" : "Pending"}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-[#2F4A78]">{r.comment}</p>
                {r.photo_url && (
                  <a href={r.photo_url} target="_blank" rel="noopener noreferrer" data-testid={`admin-review-photo-${r.id}`}>
                    <img src={r.photo_url} alt={`Photo from ${r.name}`} className="mt-3 h-24 w-32 rounded-sm object-cover" />
                  </a>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                {!r.approved && (
                  <button
                    data-testid={`admin-review-approve-${r.id}`}
                    onClick={() => approve(r.id)}
                    className="flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-4 py-2 text-xs font-semibold text-[#1B3A6B] transition-colors hover:bg-[#8A6D1F] hover:text-white"
                  >
                    <Check size={13} /> Approve
                  </button>
                )}
                <button
                  data-testid={`admin-review-delete-${r.id}`}
                  onClick={() => remove(r.id)}
                  className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                >
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
